"use client";

import React, { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* ===================== TYPES ===================== */

interface CardProps {
  title?: string;
  mediaUrl?: string; // YouTube Shorts URL
}

interface CardCarouselProps {
  data?: {
    title?: string;
    subtitle?: string;
    scroll?: CardProps[];
    isLive?: boolean;
    useH1Tag?: boolean;
  };
}

/* ===================== HELPERS ===================== */

const getYouTubeId = (url?: string) => {
  if (!url) return null;
  if (url.includes("/shorts/")) {
    return url.split("/shorts/")[1]?.split("?")[0];
  }
  const match = url.match(/[?&]v=([^&]+)/);
  return match?.[1] || null;
};

/* ===================== COMPONENT ===================== */

export default function CardCarousel({ data }: CardCarouselProps) {
  if (!data?.isLive) return null;

  const HeadingTag = data?.useH1Tag ? "h1" : "h2";
  const cards = data?.scroll ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  /* ---------- Scroll Helpers ---------- */

  const scrollByAmount = 260;

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -scrollByAmount, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: scrollByAmount, behavior: "smooth" });
  };

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    updateScrollButtons();
  }, [cards.length]);

  /* ===================== RENDER ===================== */

  return (
    <section className="py-16 bg-gradient-to-b from-[#F472B6]/10 via-slate-50 to-[#F0F7FF]">
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Header */}
        <div className="text-center mb-8">
          <HeadingTag className="text-3xl md:text-4xl font-bold text-gray-900">
            {data?.title}
          </HeadingTag>
          {data?.subtitle && (
            <p className="mt-2 text-gray-500">{data.subtitle}</p>
          )}
        </div>

        {/* Navigation Buttons */}
        {cards.length > 3 && (
          <>
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`
                absolute left-2 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 rounded-full bg-white shadow-md border
                flex items-center justify-center
                transition
                ${
                  canScrollLeft
                    ? "opacity-100 hover:scale-110"
                    : "opacity-40 cursor-not-allowed"
                }
              `}
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>

            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`
                absolute right-2 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 rounded-full bg-white shadow-md border
                flex items-center justify-center
                transition
                ${
                  canScrollRight
                    ? "opacity-100 hover:scale-110"
                    : "opacity-40 cursor-not-allowed"
                }
              `}
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </>
        )}

        {/* Scroll Row */}
        <div
          ref={scrollRef}
          onScroll={updateScrollButtons}
          className="
            flex gap-6
            overflow-x-auto
            scrollbar-hide
            pb-2
            bg-transparent
          "
        >
          {cards.map((item, index) => {
            const videoId = getYouTubeId(item?.mediaUrl);

            return (
              <div
                key={index}
                className="
                  flex-shrink-0
                  w-[220px] sm:w-[240px]
                  aspect-[9/16]
                  rounded-2xl
                  overflow-hidden
                  bg-black
                  shadow-lg
                "
              >
                {videoId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&playsinline=1&rel=0&modestbranding=1`}
                    title={item?.title || "YouTube Shorts"}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-white text-sm">
                    Video unavailable
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hide scrollbar */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </section>
  );
}
